import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Box, CircularProgress } from '@mui/material';
import { useInvitationFlowStore } from '../stores/invitationFlowStore';
import { useConfirmConsent } from '../hooks/useConfirmConsent';
import DataSharingConsentForm from '../components/DataSharingConsentForm';

/**
 * Page for obtaining data sharing consent during invitation flow
 */
const DataSharingConsentPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Get invitation data and actions from store
  const invitationInfo = useInvitationFlowStore(state => state.invitationInfo);
  const profileCaptured = useInvitationFlowStore(
    state => state.profileCaptured
  );
  const markConsentGiven = useInvitationFlowStore(
    state => state.markConsentGiven
  );

  // Check prerequisites and redirect if needed
  useEffect(() => {
    if (!id) {
      navigate('/invitation/error', { replace: true });
      return;
    }

    if (!invitationInfo) {
      // No invitation info, redirect to loading page
      navigate(`/invitation/${id}`, { replace: true });
      return;
    }

    // Check if profile was required but not captured
    if (invitationInfo.requiresProfileCapture && !profileCaptured) {
      navigate(`/invitation/${id}/profile`, { replace: true });
      return;
    }

    // Check if consent is actually required
    if (!invitationInfo.requiresDataSharingConsent) {
      // Consent not required, go to processing
      navigate(`/invitation/${id}/processing`, { replace: true });
    }
  }, [id, invitationInfo, profileCaptured, navigate]);

  // Consent confirmation mutation
  const { mutate: confirmConsent, isPending } = useConfirmConsent();

  // Handle consent given
  const handleConsent = () => {
    if (!id) return;

    confirmConsent(
      { invitationId: id },
      {
        onSuccess: () => {
          // Mark consent as given in store
          markConsentGiven();

          // Navigate to processing
          navigate(`/invitation/${id}/processing`);
        },
        onError: () => {
          // Navigate to error page on failure
          navigate(`/invitation/${id}/error`);
        },
      }
    );
  };

  // Handle consent declined
  const handleDecline = () => {
    // For decline, just navigate to declined page
    // No need to call API since consent is not given
    navigate(`/invitation/${id}/declined`);
  };

  // Show loading if prerequisites not met or processing
  if (!invitationInfo || !id || isPending) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render the consent form
  return (
    <Container maxWidth="sm">
      <DataSharingConsentForm
        organisationName={invitationInfo.organisationName}
        onConsent={handleConsent}
        onDecline={handleDecline}
      />
    </Container>
  );
};

export default DataSharingConsentPage;
